import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../store/slices/userSlice';
import { fetchChannels } from '../../store/slices/channelSlice';
import { addNotification } from '../../store/slices/uiSlice';
import PostCompletionEditor, { cloneFollowUps } from './PostCompletionEditor';
import {
  ASSIGNEE_MODES,
  FIELD_TYPES,
  POST_ACTIONS,
  SCOPE_OPTIONS,
  TEMPLATE_TYPES,
  VISIBILITY_OPTIONS,
  createMessageTemplate,
  deleteMessageTemplate,
  fetchMessageTemplates,
  extractBraceParams,
  generateTemplateApiKey,
  hasApiTemplateBody,
  setTemplateApiKeyActive,
  updateMessageTemplate,
} from '../../services/messageTemplateService';
import { API_URL } from '../../config/api';
import CompactRichEditor from '../common/CompactRichEditor';
import { uploadFilesWithProgress } from '../../utils/uploadFilesWithProgress';
import { buildLegacyFileUrl } from '../../services/fileUrlService';

const newFieldId = () => `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const emptyField = () => ({
  id: newFieldId(),
  label: '',
  fieldType: 'TEXT',
  required: true,
  publicText: false,
  options: [],
  placeholder: '',
});

const blankForm = (isAdmin = false) => ({
  name: '',
  description: '',
  templateType: 'TASK',
  fields: [emptyField()],
  assigneeMode: 'ALL_RECEIVERS',
  customAssigneeUserIds: [],
  postActions: [],
  postCreationEnabled: false,
  followUpActions: [],
  postFinishEnabled: false,
  postFinishActions: [],
  scope: isAdmin ? 'PUBLIC' : 'USER',
  visibility: 'PUBLIC',
  customChannelIds: [],
  apiBodyHtml: '',
});

const cloneFieldIds = (fields = []) => {
  const source = fields.length ? fields : [emptyField()];
  return source.map((f) => ({
    id: newFieldId(),
    label: f.label || '',
    fieldType: f.fieldType || 'TEXT',
    required: f.required !== false,
    publicText: Boolean(f.publicText),
    options: Array.isArray(f.options) ? [...f.options] : [],
    placeholder: f.placeholder || '',
  }));
};

const HamburgerIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloneIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

const CreateMessageTemplateModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.users);
  const { channels } = useSelector((state) => state.channels);
  const { user: currentUser } = useSelector((state) => state.auth);
  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'ORG_ADMIN';

  const [form, setForm] = useState(() => blankForm(false));
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [optionsDraft, setOptionsDraft] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editorTab, setEditorTab] = useState('basic');
  const [collapsedFieldIds, setCollapsedFieldIds] = useState(() => new Set());
  const [focusFieldId, setFocusFieldId] = useState(null);
  const fieldLabelRefs = useRef({});
  const fieldsListRef = useRef(null);
  const customChannelsSectionRef = useRef(null);
  const firstChannelCheckboxRef = useRef(null);
  const pendingChannelFocusRef = useRef(false);
  const [apiKeyMeta, setApiKeyMeta] = useState(null);
  const [revealedApiKey, setRevealedApiKey] = useState('');
  const [apiKeyBusy, setApiKeyBusy] = useState(false);
  const [confirmRotateKey, setConfirmRotateKey] = useState(false);

  const isApiTemplate = form.templateType === 'SEND_USING_API';
  const isMessageTemplate = form.templateType === 'MESSAGE';
  const isAppointmentTemplate = form.templateType === 'APPOINTMENT';
  const isCannedMessageTemplate = isApiTemplate || isMessageTemplate;
  const hideFollowupTab = isCannedMessageTemplate || isAppointmentTemplate;
  const apiBodyQuillRef = useRef(null);
  const selectedApiGroupId = (form.customChannelIds || [])[0] || '';
  const apiParams = useMemo(
    () => (isApiTemplate ? extractBraceParams(form.apiBodyHtml) : []),
    [isApiTemplate, form.apiBodyHtml]
  );

  const selectableChannels = useMemo(
    () => (channels || []).filter((c) => {
      if (!c || c.systemChannelKey) return false;
      const type = String(c.type || '').toUpperCase();
      return type !== 'DIRECT_MESSAGE' && type !== 'DIRECT' && type !== 'DM';
    }),
    [channels]
  );

  const otherUsers = useMemo(
    () => (users || []).filter((u) => u.id !== currentUser?.id),
    [users, currentUser?.id]
  );

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const list = await fetchMessageTemplates(true);
      setTemplates(Array.isArray(list) ? list : []);
    } catch (e) {
      dispatch(addNotification({
        type: 'error',
        message: e?.response?.data?.error || 'Failed to load templates',
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchUsers());
    dispatch(fetchChannels());
    setForm(blankForm(isAdmin));
    setEditingId(null);
    setSidebarOpen(true);
    setEditorTab('basic');
    setCollapsedFieldIds(new Set());
    setFocusFieldId(null);
    loadTemplates();
  }, [isOpen, dispatch, isAdmin]);

  useLayoutEffect(() => {
    if (!focusFieldId) return undefined;
    const input = fieldLabelRefs.current[focusFieldId];
    if (!input) return undefined;
    input.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    fieldsListRef.current?.scrollTo?.({ top: fieldsListRef.current.scrollHeight, behavior: 'smooth' });
    const timer = window.setTimeout(() => {
      input.focus({ preventScroll: true });
      input.select?.();
      setFocusFieldId(null);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [focusFieldId, form.fields?.length]);

  useLayoutEffect(() => {
    if (form.visibility !== 'CUSTOM' || !pendingChannelFocusRef.current) return undefined;
    pendingChannelFocusRef.current = false;
    const section = customChannelsSectionRef.current;
    if (!section) return undefined;
    const timer = window.setTimeout(() => {
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const checkbox = firstChannelCheckboxRef.current;
      if (checkbox) {
        checkbox.focus({ preventScroll: true });
      } else {
        section.focus({ preventScroll: true });
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [form.visibility, selectableChannels.length]);

  const handleAddField = () => {
    const field = emptyField();
    setForm((p) => ({ ...p, fields: [...(p.fields || []), field] }));
    setCollapsedFieldIds((prev) => {
      const next = new Set(prev);
      next.delete(field.id);
      return next;
    });
    setFocusFieldId(field.id);
  };

  const toggleFieldCollapsed = (fieldId) => {
    setCollapsedFieldIds((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  const handleVisibilityChange = (value) => {
    if (value === 'CUSTOM') {
      pendingChannelFocusRef.current = true;
    }
    setForm((p) => ({ ...p, visibility: value }));
  };

  const handleTemplateTypeChange = (value) => {
    if (value === 'SEND_USING_API' || value === 'MESSAGE' || value === 'APPOINTMENT') {
      setEditorTab('basic');
    }
    setForm((p) => {
      if (value === 'SEND_USING_API') {
        pendingChannelFocusRef.current = true;
        return {
          ...p,
          templateType: value,
          visibility: 'CUSTOM',
          assigneeMode: 'NONE',
          postActions: [],
          customChannelIds: (p.customChannelIds || []).slice(0, 1),
          apiBodyHtml: p.apiBodyHtml || '',
        };
      }
      if (value === 'MESSAGE') {
        return {
          ...p,
          templateType: value,
          assigneeMode: 'NONE',
          postActions: [],
          postCreationEnabled: false,
          followUpActions: [],
          postFinishEnabled: false,
          postFinishActions: [],
          apiBodyHtml: p.apiBodyHtml || '',
        };
      }
      if (value === 'APPOINTMENT') {
        return {
          ...p,
          templateType: value,
          assigneeMode: 'NONE',
          postActions: [],
          postCreationEnabled: false,
          followUpActions: [],
          postFinishEnabled: false,
          postFinishActions: [],
        };
      }
      return { ...p, templateType: value };
    });
  };

  const copyText = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(addNotification({ type: 'success', message: successMessage }));
    } catch {
      dispatch(addNotification({ type: 'error', message: 'Could not copy to clipboard' }));
    }
  };

  const handleGenerateApiKey = async () => {
    if (!editingId) {
      dispatch(addNotification({ type: 'error', message: 'Save the template first, then generate an API key' }));
      return;
    }
    if (apiKeyMeta?.hasApiKey && !confirmRotateKey) {
      setConfirmRotateKey(true);
      return;
    }
    setApiKeyBusy(true);
    try {
      const result = await generateTemplateApiKey(editingId);
      setApiKeyMeta({
        hasApiKey: true,
        apiKeyActive: Boolean(result.apiKeyActive),
        apiKeyPrefix: result.apiKeyPrefix || '',
        apiKeyCreatedAt: result.apiKeyCreatedAt || null,
      });
      setRevealedApiKey(result.apiKey || '');
      setConfirmRotateKey(false);
      dispatch(addNotification({
        type: 'success',
        message: 'API key generated. Copy it now — it will not be shown again.',
      }));
    } catch (e) {
      dispatch(addNotification({
        type: 'error',
        message: e?.response?.data?.error || 'Failed to generate API key',
      }));
    } finally {
      setApiKeyBusy(false);
    }
  };

  const handleToggleApiKeyActive = async (active) => {
    if (!editingId) return;
    setApiKeyBusy(true);
    try {
      const result = await setTemplateApiKeyActive(editingId, active);
      setApiKeyMeta((prev) => ({
        ...(prev || {}),
        hasApiKey: true,
        apiKeyActive: Boolean(result.apiKeyActive),
        apiKeyPrefix: result.apiKeyPrefix || prev?.apiKeyPrefix || '',
        apiKeyCreatedAt: result.apiKeyCreatedAt || prev?.apiKeyCreatedAt || null,
      }));
      dispatch(addNotification({
        type: 'success',
        message: active ? 'API key is active' : 'API key is inactive. Posts with this key will be rejected.',
      }));
    } catch (e) {
      dispatch(addNotification({
        type: 'error',
        message: e?.response?.data?.error || 'Failed to update API key',
      }));
    } finally {
      setApiKeyBusy(false);
    }
  };

  const updateField = (index, patch) => {
    setForm((prev) => {
      const fields = [...(prev.fields || [])];
      fields[index] = { ...fields[index], ...patch };
      return { ...prev, fields };
    });
  };

  const togglePostAction = (action) => {
    setForm((prev) => {
      const current = prev.postActions || [];
      return {
        ...prev,
        postActions: current.includes(action)
          ? current.filter((a) => a !== action)
          : [...current, action],
      };
    });
  };

  const formFromTemplate = (template, { asClone = false } = {}) => {
    const baseName = template.name || '';
    return {
      name: asClone
        ? (baseName ? `${baseName} (copy)` : 'Untitled (copy)')
        : baseName,
      description: template.description || '',
      templateType: template.templateType || 'TASK',
      fields: cloneFieldIds(template.fields),
      assigneeMode: template.assigneeMode || 'ALL_RECEIVERS',
      customAssigneeUserIds: [...(template.customAssigneeUserIds || [])],
      postActions: [...(template.postActions || [])],
      postCreationEnabled: Boolean(template.postCreationEnabled),
      followUpActions: cloneFollowUps(template.followUpActions),
      postFinishEnabled: Boolean(template.postFinishEnabled),
      postFinishActions: cloneFollowUps(template.postFinishActions),
      scope: asClone
        ? (isAdmin ? (template.scope || 'USER') : 'USER')
        : (template.scope || (isAdmin ? 'PUBLIC' : 'USER')),
      visibility: template.visibility || 'PUBLIC',
      customChannelIds: [...(template.customChannelIds || [])],
      apiBodyHtml: template.apiBodyHtml || '',
    };
  };

  const applyApiKeyMeta = (template) => {
    setApiKeyMeta(template ? {
      hasApiKey: Boolean(template.hasApiKey),
      apiKeyActive: Boolean(template.apiKeyActive),
      apiKeyPrefix: template.apiKeyPrefix || '',
      apiKeyCreatedAt: template.apiKeyCreatedAt || null,
    } : null);
    setRevealedApiKey('');
    setConfirmRotateKey(false);
  };

  const startEdit = (template) => {
    setEditingId(template.id);
    setForm(formFromTemplate(template, { asClone: false }));
    applyApiKeyMeta(template);
    setEditorTab('basic');
    setCollapsedFieldIds(new Set());
  };

  const insertPdfIntoMessageBody = async (file) => {
    if (!file) return;
    try {
      const token = localStorage.getItem('token');
      const results = await uploadFilesWithProgress({
        apiUrl: API_URL,
        token,
        files: [file],
      });
      const uploaded = results?.[0]?.data;
      const fileKey = uploaded?.fileUrl;
      if (!fileKey) throw new Error('Upload failed');
      const fileUrl = buildLegacyFileUrl(fileKey) || fileKey;
      const editor = apiBodyQuillRef.current?.getEditor?.();
      const label = file.name || 'document.pdf';
      if (editor) {
        const range = editor.getSelection(true);
        const index = range ? range.index : Math.max(0, editor.getLength() - 1);
        editor.insertText(index, label, 'link', fileUrl, 'user');
        editor.insertText(index + label.length, '\n', 'user');
        editor.setSelection(index + label.length + 1, 0);
      } else {
        setForm((p) => ({
          ...p,
          apiBodyHtml: `${p.apiBodyHtml || ''}<p><a href="${fileUrl}">${label}</a></p>`,
        }));
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.message || 'Could not attach the PDF',
      }));
    }
  };

  /** Load the same structure into the create form as a new template (does not save yet). */
  const startClone = (template) => {
    setEditingId(null);
    setForm(formFromTemplate(template, { asClone: true }));
    applyApiKeyMeta(null);
    setEditorTab('basic');
    setCollapsedFieldIds(new Set());
    setOptionsDraft({});
    dispatch(addNotification({
      type: 'info',
      message: 'Template structure copied — review and save as a new template',
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setEditorTab('basic');
      dispatch(addNotification({ type: 'error', message: 'Template name is required' }));
      return;
    }
    const fields = (form.fields || []).filter((f) => f.label?.trim());
    if (form.templateType !== 'SEND_USING_API' && form.templateType !== 'MESSAGE'
        && form.templateType !== 'APPOINTMENT' && !fields.length) {
      setEditorTab('basic');
      dispatch(addNotification({ type: 'error', message: 'Add at least one field with a label' }));
      return;
    }
    if ((form.templateType === 'SEND_USING_API' || form.templateType === 'MESSAGE')
        && !hasApiTemplateBody(form.apiBodyHtml)) {
      setEditorTab('basic');
      dispatch(addNotification({ type: 'error', message: 'Write the message in the rich text box' }));
      return;
    }
    if (form.assigneeMode === 'CUSTOM' && !(form.customAssigneeUserIds || []).length) {
      dispatch(addNotification({ type: 'error', message: 'Select at least one custom assignee' }));
      return;
    }
    if (form.templateType === 'SEND_USING_API' && (form.customChannelIds || []).length !== 1) {
      dispatch(addNotification({ type: 'error', message: 'Choose exactly one group for this API template' }));
      return;
    }
    if (form.visibility === 'CUSTOM' && !(form.customChannelIds || []).length) {
      dispatch(addNotification({ type: 'error', message: 'Select at least one channel for visibility' }));
      return;
    }
    if (form.postCreationEnabled && !hideFollowupTab) {
      const followUps = form.followUpActions || [];
      const followUpError = !followUps.length
        ? 'Add at least one post completion action'
        : followUps.reduce((found, action) => {
          if (found) return found;
          if (action.actionType === 'TEMPLATE' && !action.templateId) return 'Choose a template to trigger';
          if (action.actionType === 'TASK' && !(Number(action.estimateTimeInMinutes) > 0)) {
            return 'Each task follow-up needs an estimated time';
          }
          if (action.actionType === 'WORKFLOW' && !action.workflowReactionId) {
            return 'Choose a workflow type for each workflow follow-up';
          }
          if (action.actionType !== 'TEMPLATE' && action.actionType !== 'MESSAGE' && !action.title?.trim()) {
            return 'Each follow-up needs a title';
          }
          if (action.actionType === 'MESSAGE' && !action.body?.trim() && !(action.mentionUserIds || []).length) {
            return 'A message follow-up needs text or a mention';
          }
          if (action.actionType === 'MESSAGE' && (action.messageTarget === 'USER' || action.messageTarget === 'PERSONAL') && !(action.mentionUserIds || []).length) {
            return 'Choose who should receive the personal chat';
          }
          return null;
        }, null);
      if (followUpError) {
        setEditorTab('followup');
        dispatch(addNotification({ type: 'error', message: followUpError }));
        return;
      }
    }

    if (form.postFinishEnabled && !hideFollowupTab) {
      const finishUps = form.postFinishActions || [];
      const finishError = !finishUps.length
        ? 'Add at least one post finish action'
        : finishUps.reduce((found, action) => {
          if (found) return found;
          if (action.actionType === 'TEMPLATE' && !action.templateId) return 'Choose a template to trigger after finish';
          if (action.actionType === 'WORKFLOW' && !action.workflowReactionId) {
            return 'Choose a workflow type for each post finish workflow';
          }
          if (action.actionType !== 'TEMPLATE' && action.actionType !== 'MESSAGE' && !action.title?.trim()) {
            return 'Each post finish action needs a title';
          }
          if (action.actionType === 'MESSAGE' && (action.messageTarget === 'USER' || action.messageTarget === 'PERSONAL') && !(action.mentionUserIds || []).length) {
            return 'Choose who should receive the personal chat after finish';
          }
          return null;
        }, null);
      if (finishError) {
        setEditorTab('followup');
        dispatch(addNotification({ type: 'error', message: finishError }));
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        description: form.description?.trim() || '',
        fields,
        scope: isAdmin ? (form.scope || 'USER') : 'USER',
        postCreationEnabled: Boolean(form.postCreationEnabled),
        followUpActions: form.postCreationEnabled ? (form.followUpActions || []) : [],
        postFinishEnabled: Boolean(form.postFinishEnabled),
        postFinishActions: form.postFinishEnabled ? (form.postFinishActions || []) : [],
      };
      if (payload.templateType === 'SEND_USING_API') {
        payload.visibility = 'CUSTOM';
        payload.assigneeMode = 'NONE';
        payload.postActions = [];
        payload.customChannelIds = (form.customChannelIds || []).slice(0, 1);
        payload.apiBodyHtml = form.apiBodyHtml || '';
        payload.fields = [];
      }
      if (payload.templateType === 'MESSAGE') {
        payload.assigneeMode = 'NONE';
        payload.postActions = [];
        payload.postCreationEnabled = false;
        payload.followUpActions = [];
        payload.postFinishEnabled = false;
        payload.postFinishActions = [];
        payload.apiBodyHtml = form.apiBodyHtml || '';
        payload.fields = [];
      }
      if (payload.templateType === 'APPOINTMENT') {
        payload.assigneeMode = 'NONE';
        payload.postActions = [];
        payload.postCreationEnabled = false;
        payload.followUpActions = [];
        payload.postFinishEnabled = false;
        payload.postFinishActions = [];
      }
      const saved = editingId
        ? await updateMessageTemplate(editingId, payload)
        : await createMessageTemplate(payload);
      dispatch(addNotification({
        type: 'success',
        message: editingId ? 'Template updated' : 'Template created',
      }));
      await loadTemplates();
      if (payload.templateType === 'SEND_USING_API' && saved?.id) {
        startEdit(saved);
      } else {
        setForm(blankForm(isAdmin));
        setEditingId(null);
        applyApiKeyMeta(null);
        setEditorTab('basic');
        setCollapsedFieldIds(new Set());
      }
    } catch (e) {
      dispatch(addNotification({
        type: 'error',
        message: e?.response?.data?.error || 'Failed to save template',
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this template?')) return;
    try {
      await deleteMessageTemplate(id);
      dispatch(addNotification({ type: 'success', message: 'Template deactivated' }));
      if (editingId === id) {
        setEditingId(null);
        setForm(blankForm(isAdmin));
        applyApiKeyMeta(null);
      }
      await loadTemplates();
    } catch (e) {
      dispatch(addNotification({
        type: 'error',
        message: e?.response?.data?.error || 'Failed to delete template',
      }));
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-start justify-center p-4 sm:p-6">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-5xl my-6 overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => setSidebarOpen((open) => !open)}
                className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={sidebarOpen ? 'Hide templates list' : 'Show templates list'}
                aria-label={sidebarOpen ? 'Hide templates list' : 'Show templates list'}
                aria-expanded={sidebarOpen}
              >
                <HamburgerIcon />
              </button>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {editingId ? 'Edit Message Template' : 'Create Message Template'}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 hidden sm:block">
                  Templates appear in the chat rich text box for structured posts.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="flex h-[min(80vh,720px)] min-h-0 relative">
            {/* Backdrop when sidebar open on small screens */}
            {sidebarOpen && (
              <button
                type="button"
                className="absolute inset-0 z-10 bg-black/30 lg:hidden"
                aria-label="Close templates list"
                onClick={() => setSidebarOpen(false)}
              />
            )}

            <aside
              className={`
                absolute lg:static inset-y-0 left-0 z-20
                w-[min(100%,18rem)] lg:w-72 flex-shrink-0
                border-r border-gray-200 dark:border-gray-700
                bg-white dark:bg-gray-800
                overflow-y-auto p-4 space-y-2
                transition-transform duration-200 ease-out
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:hidden'}
                ${sidebarOpen ? 'lg:block' : ''}
              `}
              aria-hidden={!sidebarOpen}
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Your templates</h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setForm(blankForm(isAdmin));
                      applyApiKeyMeta(null);
                      setEditorTab('basic');
                      setCollapsedFieldIds(new Set());
                    }}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    New
                  </button>
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden"
                    title="Close list"
                    aria-label="Close templates list"
                  >
                    ✕
                  </button>
                </div>
              </div>
              {loading && <p className="text-xs text-gray-500">Loading…</p>}
              {!loading && templates.length === 0 && (
                <p className="text-xs text-gray-500">No templates yet.</p>
              )}
              {templates.map((t) => (
                <div
                  key={t.id}
                  className={`rounded-lg border p-3 ${
                    editingId === t.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  } ${!t.active ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" className="text-left flex-1 min-w-0" onClick={() => startEdit(t)}>
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{t.name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {(TEMPLATE_TYPES.find((x) => x.value === t.templateType)?.label || t.templateType)}
                        {' · '}
                        {t.templateType === 'SEND_USING_API'
                          ? 'API'
                          : (t.scope === 'PUBLIC' ? 'Public' : 'Only me')}
                        {t.templateType !== 'SEND_USING_API' ? ` · ${t.visibility}` : ''}
                        {!t.active ? ' · inactive' : ''}
                      </div>
                    </button>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          startClone(t);
                        }}
                        className="p-1.5 rounded-md text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Clone template structure"
                        aria-label={`Clone ${t.name || 'template'}`}
                      >
                        <CloneIcon />
                      </button>
                      {t.active && (
                        <button
                          type="button"
                          onClick={() => handleDelete(t.id)}
                          className="text-[11px] text-rose-500 hover:underline px-1"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </aside>

            <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-5 min-h-0">
              <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 -mt-1 overflow-x-auto">
                {[
                  { id: 'basic', label: 'Basic info' },
                  ...(!hideFollowupTab ? [{
                    id: 'followup',
                    label: 'Post completion & Post finish',
                    count: (form.postCreationEnabled ? (form.followUpActions || []).length : 0)
                      + (form.postFinishEnabled ? (form.postFinishActions || []).length : 0),
                  }] : []),
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setEditorTab(tab.id)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                      editorTab === tab.id
                        ? 'border-primary-500 text-primary-600 dark:text-primary-300'
                        : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className="rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200 px-1.5 text-[10px]">
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {editorTab === 'basic' && (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="e.g. Bug intake"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Template Type</label>
                  <select
                    value={form.templateType}
                    onChange={(e) => handleTemplateTypeChange(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  >
                    {TEMPLATE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  {isAppointmentTemplate && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                      When used in chat, this asks for subject, start time, duration, and participants. Extra fields go into the appointment description.
                    </p>
                  )}
                </div>
                {!isCannedMessageTemplate && !isAppointmentTemplate && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Assignee</label>
                  <select
                    value={form.assigneeMode}
                    onChange={(e) => setForm((p) => ({ ...p, assigneeMode: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  >
                    {ASSIGNEE_MODES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                )}
                {!isApiTemplate && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder={isMessageTemplate
                      ? 'Optional note shown before sending'
                      : isAppointmentTemplate
                        ? 'Optional default notes. Extra fields below are added to the appointment description.'
                        : 'Optional helper text shown when filling the template'}
                  />
                </div>
                )}
              </div>

              {!isCannedMessageTemplate && form.assigneeMode === 'CUSTOM' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Custom assignees</label>
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-1">
                    {otherUsers.map((u) => {
                      const checked = (form.customAssigneeUserIds || []).includes(u.id);
                      return (
                        <label key={u.id} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm((p) => {
                                const ids = new Set(p.customAssigneeUserIds || []);
                                if (ids.has(u.id)) ids.delete(u.id);
                                else ids.add(u.id);
                                return { ...p, customAssigneeUserIds: [...ids] };
                              });
                            }}
                          />
                          {(u.firstName || '') + ' ' + (u.lastName || '') || u.username}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {isApiTemplate && (
                <>
                  <div
                    ref={customChannelsSectionRef}
                    tabIndex={-1}
                    className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                  >
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Which group are you sending to?
                    </label>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                      This template posts only in the group you pick, as you.
                    </p>
                    <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-1">
                      {selectableChannels.map((c, channelIndex) => {
                        const checked = selectedApiGroupId === c.id;
                        return (
                          <label key={c.id} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                            <input
                              ref={channelIndex === 0 ? firstChannelCheckboxRef : undefined}
                              type="radio"
                              name="api-template-group"
                              checked={checked}
                              onChange={() => setForm((p) => ({ ...p, customChannelIds: [c.id] }))}
                            />
                            {c.name}
                          </label>
                        );
                      })}
                      {selectableChannels.length === 0 && (
                        <p className="text-xs text-gray-500 px-1 py-2">No groups available.</p>
                      )}
                    </div>
                  </div>

                  {selectedApiGroupId ? (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          Message
                        </label>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                          Write the post in this box. Wrap values callers must send in curly braces, e.g. Hello {'{name}'}.
                        </p>
                        <CompactRichEditor
                          quillRef={apiBodyQuillRef}
                          value={form.apiBodyHtml || ''}
                          onChange={(html) => setForm((p) => ({ ...p, apiBodyHtml: html }))}
                          placeholder="Type the group message. Use {param} for values the API must send…"
                          minHeight={160}
                          maxHeight={280}
                          instanceClass="api-template-quill"
                          enableMentions={false}
                          enableHtmlSourceToggle
                        />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          Parameters to send
                        </p>
                        {apiParams.length === 0 ? (
                          <p className="text-[11px] text-gray-500">
                            No {'{params}'} yet. This posts as a static message.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {apiParams.map((param) => (
                              <span
                                key={param}
                                className="inline-flex items-center rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200 px-2.5 py-1 text-[11px] font-mono"
                              >
                                {param}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Select a group to open the message editor.
                    </p>
                  )}
                </>
              )}

              {isMessageTemplate && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Message content
                    </label>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                      This posts as a normal chat message. Add text, images, and PDFs.
                    </p>
                    <CompactRichEditor
                      quillRef={apiBodyQuillRef}
                      value={form.apiBodyHtml || ''}
                      onChange={(html) => setForm((p) => ({ ...p, apiBodyHtml: html }))}
                      placeholder="Write the message…"
                      minHeight={180}
                      maxHeight={320}
                      instanceClass="message-template-quill"
                      enableMentions={false}
                      onPasteFiles={(files) => {
                        const pdf = (files || []).find((file) =>
                          (file.type || '').includes('pdf') || /\.pdf$/i.test(file.name || '')
                        );
                        if (pdf) insertPdfIntoMessageBody(pdf);
                      }}
                    />
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      id="message-template-pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) insertPdfIntoMessageBody(file);
                      }}
                    />
                    <label
                      htmlFor="message-template-pdf"
                      className="inline-flex cursor-pointer items-center rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Attach PDF
                    </label>
                  </div>
                </div>
              )}

              {!isCannedMessageTemplate && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50/80 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Fields</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {(form.templateType === 'TASK' || form.templateType === 'WORKFLOW')
                        ? 'Use “In title” on values that should become the task or workflow title.'
                        : isAppointmentTemplate
                          ? 'Optional. Answers are added to the appointment description. Subject, start time, duration, and participants are always asked when posting.'
                          : 'These are the questions someone fills when using this template.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {(form.fields || []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const ids = (form.fields || []).map((field) => field.id);
                          const allCollapsed = ids.every((id) => collapsedFieldIds.has(id));
                          setCollapsedFieldIds(allCollapsed ? new Set() : new Set(ids));
                        }}
                        className="text-[11px] font-medium text-primary-600 dark:text-primary-300 hover:underline"
                      >
                        {(form.fields || []).every((field) => collapsedFieldIds.has(field.id)) ? 'Expand all' : 'Collapse all'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleAddField}
                      className="text-xs rounded-lg bg-primary-600 text-white px-2.5 py-1.5 hover:bg-primary-700"
                    >
                      + Add field
                    </button>
                  </div>
                </div>
                <div ref={fieldsListRef} className="p-3 space-y-2">
                  {(form.fields || []).map((field, index) => {
                    const collapsed = collapsedFieldIds.has(field.id);
                    const typeLabel = FIELD_TYPES.find((t) => t.value === field.fieldType)?.label || field.fieldType;
                    return (
                      <div key={field.id} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2.5">
                          <button
                            type="button"
                            onClick={() => toggleFieldCollapsed(field.id)}
                            className="flex items-center gap-2 flex-1 min-w-0 text-left"
                            aria-expanded={!collapsed}
                          >
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${collapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200 text-[11px] font-semibold">
                              {index + 1}
                            </span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {field.label || 'Untitled field'}
                            </span>
                            <span className="hidden sm:inline text-[11px] rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-0.5">
                              {typeLabel}
                            </span>
                            {field.required !== false && (
                              <span className="hidden sm:inline text-[11px] text-gray-400">Required</span>
                            )}
                            {field.publicText && (
                              <span className="hidden sm:inline text-[11px] text-primary-600 dark:text-primary-300">In title</span>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setForm((p) => ({
                              ...p,
                              fields: p.fields.filter((_, i) => i !== index),
                            }))}
                            className="text-[11px] text-rose-500 hover:underline flex-shrink-0"
                          >
                            Remove
                          </button>
                        </div>
                        {!collapsed && (
                          <div className="px-3 pb-3 space-y-3 border-t border-gray-100 dark:border-gray-700">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-3">
                              <label className="sm:col-span-2 block">
                                <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Label</span>
                                <input
                                  ref={(el) => {
                                    if (el) fieldLabelRefs.current[field.id] = el;
                                    else delete fieldLabelRefs.current[field.id];
                                  }}
                                  value={field.label}
                                  onChange={(e) => updateField(index, { label: e.target.value })}
                                  placeholder="What should this be called?"
                                  className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                                />
                              </label>
                              <label className="block">
                                <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Type</span>
                                <select
                                  value={field.fieldType}
                                  onChange={(e) => updateField(index, { fieldType: e.target.value })}
                                  className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                                >
                                  {FIELD_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                                </select>
                              </label>
                            </div>
                            <label className="block">
                              <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Placeholder</span>
                              <input
                                value={field.placeholder || ''}
                                onChange={(e) => updateField(index, { placeholder: e.target.value })}
                                placeholder="Hint shown while filling this field"
                                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                              />
                            </label>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => updateField(index, { required: field.required === false })}
                                className={`text-xs rounded-full border px-2.5 py-1 ${
                                  field.required !== false
                                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                                }`}
                              >
                                Required
                              </button>
                              {(form.templateType === 'TASK' || form.templateType === 'WORKFLOW') && (
                                <button
                                  type="button"
                                  onClick={() => updateField(index, { publicText: !field.publicText })}
                                  className={`text-xs rounded-full border px-2.5 py-1 ${
                                    field.publicText
                                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                                  }`}
                                >
                                  In title
                                </button>
                              )}
                            </div>
                            {(form.templateType === 'TASK' || form.templateType === 'WORKFLOW') && field.publicText && (
                              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                This value is included in the task / workflow title. Multiple title fields are combined.
                              </p>
                            )}
                            {field.fieldType === 'SELECT' && (
                              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                                <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400">Options</span>
                                <div className="flex gap-2">
                                  <input
                                    value={optionsDraft[field.id] || ''}
                                    onChange={(e) => setOptionsDraft((p) => ({ ...p, [field.id]: e.target.value }))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const value = (optionsDraft[field.id] || '').trim();
                                        if (!value) return;
                                        updateField(index, {
                                          options: [...(field.options || []), value],
                                        });
                                        setOptionsDraft((p) => ({ ...p, [field.id]: '' }));
                                      }
                                    }}
                                    placeholder="Add an option"
                                    className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const value = (optionsDraft[field.id] || '').trim();
                                      if (!value) return;
                                      updateField(index, {
                                        options: [...(field.options || []), value],
                                      });
                                      setOptionsDraft((p) => ({ ...p, [field.id]: '' }));
                                    }}
                                    className="text-xs rounded-lg border border-gray-300 dark:border-gray-600 px-3 text-gray-700 dark:text-gray-200"
                                  >
                                    Add
                                  </button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {(field.options || []).map((opt) => (
                                    <span
                                      key={opt}
                                      className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 text-[11px] text-gray-700 dark:text-gray-200"
                                    >
                                      {opt}
                                      <button
                                        type="button"
                                        className="text-gray-400 hover:text-rose-500"
                                        onClick={() => updateField(index, {
                                          options: (field.options || []).filter((o) => o !== opt),
                                        })}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                  {(field.options || []).length === 0 && (
                                    <p className="text-[11px] text-gray-400">No options yet.</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(form.fields || []).length === 0 && (
                    <p className="text-xs text-gray-500 px-1 py-2">No fields yet. Add one to collect answers when this template is used.</p>
                  )}
                </div>
              </div>
              )}


              {isApiTemplate ? (
                selectedApiGroupId ? (
                <>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">API key</h4>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        Only you can generate this key. Posts are accepted only while the key is active.
                      </p>
                    </div>
                    {!editingId && (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        Save the template first, then generate the API key.
                      </p>
                    )}
                    {editingId && apiKeyMeta?.hasApiKey && (
                      <p className="text-xs text-gray-600 dark:text-gray-300">
                        Key prefix <span className="font-mono">{apiKeyMeta.apiKeyPrefix || 'wl_…'}</span>
                        {apiKeyMeta.apiKeyActive ? ' · active' : ' · inactive'}
                      </p>
                    )}
                    {revealedApiKey && (
                      <div className="space-y-2">
                        <label className="block text-[11px] font-medium text-gray-500">
                          Copy this key now. It is not shown again.
                        </label>
                        <div className="flex gap-2">
                          <input
                            readOnly
                            value={revealedApiKey}
                            className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-xs font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => copyText(revealedApiKey, 'API key copied')}
                            className="text-xs rounded-lg border border-gray-300 dark:border-gray-600 px-3 text-gray-700 dark:text-gray-200"
                          >
                            Copy
                          </button>
                        </div>
                        <pre className="text-[10px] whitespace-pre-wrap rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-2 text-gray-700 dark:text-gray-300 overflow-x-auto">
{`curl -X POST ${API_URL}/public/templates/${editingId}/messages \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ${revealedApiKey}" \\
  -d '{"fieldValues":{${apiParams.map((p) => `"${p}":"..."`).join(',')}}}'`}
                        </pre>
                      </div>
                    )}
                    {confirmRotateKey && (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        Generating again replaces the current key. Click Generate once more to confirm.
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={!editingId || apiKeyBusy}
                        onClick={handleGenerateApiKey}
                        className="text-xs rounded-lg bg-primary-600 text-white px-3 py-1.5 disabled:opacity-50"
                      >
                        {apiKeyBusy
                          ? 'Working…'
                          : (apiKeyMeta?.hasApiKey ? 'Generate new API key' : 'Generate API key')}
                      </button>
                      {editingId && apiKeyMeta?.hasApiKey && (
                        <button
                          type="button"
                          disabled={apiKeyBusy}
                          onClick={() => handleToggleApiKeyActive(!apiKeyMeta.apiKeyActive)}
                          className="text-xs rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                        >
                          {apiKeyMeta.apiKeyActive ? 'Deactivate key' : 'Activate key'}
                        </button>
                      )}
                    </div>
                  </div>
                </>
                ) : null
              ) : (
                <>
              {!isMessageTemplate && !isAppointmentTemplate && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Post Actions</label>
                <div className="flex flex-wrap gap-3">
                  {POST_ACTIONS.map((action) => (
                    <label key={action.value} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                      <input
                        type="checkbox"
                        checked={(form.postActions || []).includes(action.value)}
                        onChange={() => togglePostAction(action.value)}
                      />
                      {action.label}
                    </label>
                  ))}
                </div>
              </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Who can use this
                </label>
                {isAdmin ? (
                  <select
                    value={form.scope || 'USER'}
                    onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  >
                    {SCOPE_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                    Only me
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      Admins can create public templates for everyone.
                    </span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Where it can be posted
                </label>
                <select
                  value={form.visibility}
                  onChange={(e) => handleVisibilityChange(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                >
                  {VISIBILITY_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {form.visibility === 'CUSTOM' && (
                <div
                  ref={customChannelsSectionRef}
                  tabIndex={-1}
                  className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                >
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Visible in</label>
                  <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-1">
                    {selectableChannels.map((c, channelIndex) => {
                      const checked = (form.customChannelIds || []).includes(c.id);
                      return (
                        <label key={c.id} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                          <input
                            ref={channelIndex === 0 ? firstChannelCheckboxRef : undefined}
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm((p) => {
                                const ids = new Set(p.customChannelIds || []);
                                if (ids.has(c.id)) ids.delete(c.id);
                                else ids.add(c.id);
                                return { ...p, customChannelIds: [...ids] };
                              });
                            }}
                          />
                          {c.name}
                          <span className="text-[10px] text-gray-400">
                            {c.type === 'DIRECT_MESSAGE' ? 'DM' : 'Channel'}
                          </span>
                        </label>
                      );
                    })}
                    {selectableChannels.length === 0 && (
                      <p className="text-xs text-gray-500 px-1 py-2">No channels available.</p>
                    )}
                  </div>
                </div>
              )}
                </>
              )}
              </>
              )}

              {editorTab === 'followup' && !hideFollowupTab && (
                <div className="space-y-4">
                  <PostCompletionEditor
                    enabled={form.postCreationEnabled}
                    actions={form.followUpActions}
                    users={users}
                    channels={selectableChannels}
                    excludeTemplateId={editingId}
                    title="Post completion"
                    description="After this template is posted, also create tasks, messages, workflows, announcements, or trigger another template."
                    onChange={({ enabled, actions }) => setForm((p) => ({
                      ...p,
                      postCreationEnabled: enabled,
                      followUpActions: actions,
                    }))}
                  />
                  <PostCompletionEditor
                    enabled={form.postFinishEnabled}
                    actions={form.postFinishActions}
                    users={users}
                    channels={selectableChannels}
                    excludeTemplateId={editingId}
                    title="Post finish"
                    description="Copied onto tasks and workflows created from this template. Runs when someone marks that task or workflow complete."
                    onChange={({ enabled, actions }) => setForm((p) => ({
                      ...p,
                      postFinishEnabled: enabled,
                      postFinishActions: actions,
                    }))}
                  />
                </div>
              )}
              </div>

              <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                >
                  Close
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleSave}
                  className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingId ? 'Update template' : 'Create template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateMessageTemplateModal;
